<template>
  <div>
    <p class="small text-muted mb-3">{{ note }}</p>

    <div class="d-flex align-items-center gap-2 small" aria-live="polite">
      <template v-if="isSaving">
        <span class="fa fa-spinner fa-spin text-muted" aria-hidden="true"></span>
        <span class="text-muted">{{ savingLabel }}</span>
      </template>
      <template v-else-if="saveErrorMessage">
        <span class="text-danger">{{ saveErrorLabel }}{{ saveErrorMessage ? `: ${saveErrorMessage}` : "" }}</span>
      </template>
      <template v-else-if="isSaved">
        <span class="text-success">{{ savedLabel }}</span>
      </template>
      <template v-else-if="hasPendingChanges">
        <span class="text-warning-emphasis">{{ unsavedLabel }}</span>
      </template>
    </div>

    <form @submit.prevent>
      <div class="mb-3">
        <label class="form-label">{{ nameLabel }}</label>
        <input v-model="title" type="text" class="form-control" />
      </div>

      <div class="mb-3">
        <label class="form-label">{{ categoryLabel }}</label>
        <select v-model="guideCategoryId" class="form-select">
          <option value="">{{ selectCategoryLabel }}</option>
          <option v-for="category in categoryOptions" :key="category.id" :value="String(category.id)">
            {{ category.name }}
          </option>
        </select>
      </div>

      <div class="mb-3">
        <label class="form-label">{{ descriptionLabel }}</label>
        <textarea v-model="description" class="form-control" rows="4"></textarea>
      </div>

    </form>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue"

const props = defineProps({
  offcanvasId: { type: String, required: true },
  updateUrl: { type: String, required: true },
  title: { type: String, default: "" },
  description: { type: String, default: "" },
  guideCategoryId: { type: String, default: "" },
  categories: { type: String, default: "[]" },
  note: { type: String, default: "" },
  nameLabel: { type: String, default: "Name" },
  categoryLabel: { type: String, default: "Category" },
  descriptionLabel: { type: String, default: "Description" },
  selectCategoryLabel: { type: String, default: "Select a category" },
  savingLabel: { type: String, default: "Saving..." },
  savedLabel: { type: String, default: "Changes saved" },
  unsavedLabel: { type: String, default: "Unsaved changes" },
  unsavedConfirm: { type: String, default: "You have unsaved changes. Close anyway?" },
  saveErrorLabel: { type: String, default: "Unable to save changes" },
})

const title = ref(props.title)
const description = ref(props.description)
const guideCategoryId = ref(props.guideCategoryId || "")

const lastSaved = ref({
  title: props.title,
  description: props.description,
  guideCategoryId: props.guideCategoryId || "",
})

const isSaving = ref(false)
const isSaved = ref(false)
const saveErrorMessage = ref("")
let saveTimer = null
let offcanvasElement = null

const categoryOptions = computed(() => {
  try {
    return JSON.parse(props.categories || "[]")
  } catch (_) {
    return []
  }
})

const hasPendingChanges = computed(() => {
  return title.value !== lastSaved.value.title ||
    description.value !== lastSaved.value.description ||
    guideCategoryId.value !== lastSaved.value.guideCategoryId
})

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content || ""
}

async function saveNow() {
  if (!hasPendingChanges.value || isSaving.value) {
    return
  }

  isSaving.value = true
  isSaved.value = false
  saveErrorMessage.value = ""

  const formData = new FormData()
  formData.append("guide[title]", title.value)
  formData.append("guide[description]", description.value)
  formData.append("guide[guide_category_id]", guideCategoryId.value)

  try {
    const response = await fetch(props.updateUrl, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      credentials: "same-origin",
      body: formData,
    })

    const payload = await response.json()

    if (!response.ok) {
      saveErrorMessage.value = payload?.errors?.join(", ") || ""
      return
    }

    lastSaved.value = {
      title: title.value,
      description: description.value,
      guideCategoryId: guideCategoryId.value,
    }
    isSaved.value = true
    window.setTimeout(() => {
      isSaved.value = false
    }, 1500)
  } catch (_) {
    saveErrorMessage.value = ""
  } finally {
    isSaving.value = false
  }
}

function scheduleSave() {
  isSaved.value = false
  saveErrorMessage.value = ""

  if (saveTimer) {
    window.clearTimeout(saveTimer)
  }

  saveTimer = window.setTimeout(() => {
    saveTimer = null
    saveNow()
  }, 500)
}

function handleOffcanvasHide(event) {
  const hasQueuedSave = Boolean(saveTimer)
  const hasUnsavedChanges = hasPendingChanges.value || isSaving.value || hasQueuedSave
  if (!hasUnsavedChanges) {
    return
  }

  const okToClose = window.confirm(props.unsavedConfirm)
  if (!okToClose) {
    event.preventDefault()
    return
  }

  if (saveTimer) {
    window.clearTimeout(saveTimer)
    saveTimer = null
  }
}

watch([title, description, guideCategoryId], scheduleSave)

onMounted(() => {
  offcanvasElement = document.getElementById(props.offcanvasId)
  if (offcanvasElement) {
    offcanvasElement.addEventListener("hide.bs.offcanvas", handleOffcanvasHide)
  }
})

onBeforeUnmount(() => {
  if (saveTimer) {
    window.clearTimeout(saveTimer)
  }

  if (offcanvasElement) {
    offcanvasElement.removeEventListener("hide.bs.offcanvas", handleOffcanvasHide)
  }
})
</script>
